import { PractitionerDto } from '@ecdlink/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import {
  getAllPractitioners,
  updatePractitionerRegistered,
  updatePractitionerProgress,
  deActivatePractitioner,
  updatePractitionerUsePhotoInReport,
  updatePractitionerBusinessWalkThrough,
  getPractitionerByUserId,
  updatePractitionerShareInfo,
  updatePrincipalInvitation,
  updatePractitionerMaxChildren,
  updatePractitionerIsPrincipal,
} from './practitioner.actions';
import {
  PractitionerState,
  PrincipalPractitioners,
} from './practitioner.types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';

const initialState: PractitionerState = {
  practitioner: undefined,
  practitioners: undefined,
  principalPractitioners: undefined,
  practitionersMetrics: undefined,
};

const practitionerSlice = createSlice({
  name: 'practitioner',
  initialState,
  reducers: {
    resetPractitionerState: (state) => {
      state.practitioner = initialState.practitioner;
      state.practitioners = initialState.practitioners;
      state.principalPractitioners = initialState.principalPractitioners;
    },
    addPrincipalPractitioners: (
      state,
      action: PayloadAction<PrincipalPractitioners[]>
    ) => {
      state.principalPractitioners = action.payload?.map((x) => ({
        ...x,
        synced: false,
      }));
    },
    updatePractitioner: (state, action: PayloadAction<PractitionerDto>) => {
      if (state.practitioner) {
        state.practitioner = { ...action.payload, synced: false };
      }
    },
    updateClubForPractitioner: (
      state,
      action: PayloadAction<{ practitionerId: string; clubId: string }>
    ) => {
      if (!state.practitioners) {
        return;
      }

      const practitioner = state.practitioners.find(
        (practitioner) => practitioner.id === action.payload.practitionerId
      );

      if (!!practitioner) {
        state.practitioners = [
          ...state.practitioners?.filter(
            (practitioner) => practitioner.id !== action.payload.practitionerId
          ),
          {
            ...practitioner,
            clubId: action.payload.clubId,
          },
        ];
      }
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, deActivatePractitioner);
    setThunkActionStatus(builder, getAllPractitioners);
    builder.addCase(getPractitionerByUserId.fulfilled, (state, action) => {
      state.practitioner = { ...action.payload, synced: true };
    });
    builder.addCase(getAllPractitioners.fulfilled, (state, action) => {
      state.practitioners = action.payload?.map((x) => ({
        ...x,
        synced: true,
      }));
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updatePractitionerRegistered.fulfilled, (state) => {
      state.practitioner = {
        ...state.practitioner,
        isRegistered: true,
        synced: true,
      };
    });
    builder.addCase(updatePractitionerShareInfo.fulfilled, (state) => {
      state.practitioner = {
        ...state.practitioner,
        shareInfo: true,
        synced: true,
      };
    });
    builder.addCase(updatePractitionerProgress.fulfilled, (state, action) => {
      state.practitioner = {
        ...state.practitioner,
        progress: action.payload,
        synced: true,
      };
    });
    builder.addCase(
      updatePractitionerMaxChildren.fulfilled,
      (state, action) => {
        state.practitioner = {
          ...state.practitioner,
          maxChildren: action.payload,
          synced: true,
        };
      }
    );
    builder.addCase(
      updatePractitionerIsPrincipal.fulfilled,
      (state, action) => {
        state.practitioner = {
          ...state.practitioner,
          isPrincipal: action.payload,
          synced: true,
        };
      }
    );
    builder.addCase(updatePrincipalInvitation.fulfilled, (state, action) => {
      const { principalHierarchy, accepted } = action.meta.arg;
      state.practitioner = {
        ...state.practitioner,
        // The principal link is only created server side, so mirror it here on
        // acceptance. Without this the cached practitioner stays unlinked until
        // the next getPractitionerByUserId.
        ...(accepted && principalHierarchy ? { principalHierarchy } : {}),
        dateAccepted: action.payload?.acceptedDate,
        synced: true,
      };
    });
    builder.addCase(deActivatePractitioner.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);
      state.practitioners = state.practitioners?.filter(
        (x) => x.userId !== action.meta.arg.userId
      );
    });
    builder.addCase(
      updatePractitionerBusinessWalkThrough.fulfilled,
      (state, action) => {
        state.practitioner = {
          ...state.practitioner,
          isCompletedBusinessWalkThrough: action.payload,
          synced: true,
        };
      }
    );
    builder.addCase(
      updatePractitionerUsePhotoInReport.fulfilled,
      (state, action) => {
        state.practitioner = {
          ...state.practitioner,
          usePhotoInReport: action.payload,
          synced: true,
        };
      }
    );
  },
});

const { reducer: practitionerReducer, actions: practitionerActions } =
  practitionerSlice;

const practitionerPersistConfig = {
  key: 'practitioner',
  storage: localForage,
  blacklist: [],
};

export { practitionerPersistConfig, practitionerReducer, practitionerActions };
